import React, { useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { Trans, t } from '@lingui/macro';
import { ExternalLink, FileText, Edit2, Trash2, Pin, PinOff, Inbox, Copy, Loader2 } from "lucide-react";

import { RoomItem } from "../../models/RoomItem";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { pojo } from "../../services/Utils/Util";
import { FileIcon } from "./MessageFileItem";
import { useAppUIContext } from "../../context/AppUIContext";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useToast } from "../../components/ui/use-toast";
import { Label } from "../../components/ui/label";

export const ItemPopover: React.FC<{ item: RoomItem, canEdit: boolean, compact?: boolean, children?: React.ReactNode }> = ({ item, canEdit, compact = false, children }) => {
  const { toast } = useToast();
  const history = useHistory();
  const { setMessageToShow, setIframeItem } = useAppUIContext();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(item.title || "");
  const [isOpening, setIsOpening] = useState(false);

  const formatedUrl = item.url?.replace('https://', '').replace('http://', '');

  // Handlers
  const handleOpenLink = async () => {
    if (item.url?.includes(window.location.hostname) && item.url?.includes('/invitation/')) {
      history.push(UtilApp.getPathFromURL(item.url));
    } else if (item.url) {
      setIsOpening(true);
      try {
        const res = await Fire.item.getRoomItemDownloadURL(item.roomId, item.id);
        if (res.status === 'SUCCESS' && res.url) {
          // Server sets responseDisposition: attachment, so this will trigger download
          window.open(res.url, '_blank');
        } else {
          toast({
            variant: "destructive",
            title: t`Download Failed`,
            description: res.status === 'NOT_MEMBER' ? t`You don't have permission.` : t`File not found or expired.`,
          });
        }
      } catch (error) {
        console.error("Failed to get download URL", error);
        toast({
          variant: "destructive",
          title: t`Error`,
          description: (error as Error).message,
        });
      } finally {
        setIsOpening(false);
      }
    }
  };

  const handleShowMessage = () => {
    if (item.message) setMessageToShow(item.message);
  };

  const handleCopyToMyRoom = async () => {
    let copy = pojo(item) as RoomItem;
    // copy.id = undefined; 
    copy.roomId = My.user.id;
    copy.pinned = false;
    copy.created = new Date();

    await Fire.item.saveRoomItem(copy);
    toast({ title: t`Copied to My Room`, duration: 2000 });
  };

  const handleTogglePin = () => {
    item.pinned = !item.pinned;
    Fire.item.saveRoomItem(item);
  };

  const handleDelete = () => {
    Fire.item.delete(item).then(() => {
      setDeleteAlertOpen(false);
      toast({ title: t`Item deleted` });
    });
  };

  const handleSaveTitle = () => {
    if (editingTitle && editingTitle !== item.title) {
      item.title = editingTitle;
      Fire.item.saveRoomItem(item).then(() => {
        setEditDialogOpen(false);
        toast({ title: t`Title updated` });
      });
    } else {
      setEditDialogOpen(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {children ? (
            children
          ) : (
            <div className="relative flex items-center gap-3 py-2 px-3 -mx-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors group outline-none">
              {/* Pin Icon */}
              {item.pinned && <Pin className="absolute top-0.5 left-0.5 w-3.5 h-3.5 text-orange-500 fill-current transform -rotate-45" />}

              {/* File Icon */}
              {item.type === 'file' && (
                <div className="shrink-0">
                  <FileIcon filename={item.url?.split('?')[0] ?? ''} />
                </div>
              )}

              {/* Meta */}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-gray-900 line-clamp-1 break-all" title={item.title || formatedUrl}>
                  {item.title || formatedUrl}
                </p>
                {!compact && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate flex items-center gap-1">
                    <span>{item.user.name}</span>
                    <span className="text-gray-300">•</span>
                    <span>{UtilApp.dateStrToDay(item.created, My.user.lang)}</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {item.type !== 'folder' && (
            <>
              <DropdownMenuItem onClick={handleOpenLink} className="gap-2" disabled={isOpening}>
                {isOpening ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                <Trans>Open new tab</Trans>
              </DropdownMenuItem>
              {item.message && (
                <DropdownMenuItem onClick={handleShowMessage} className="gap-2">
                  <FileText className="w-4 h-4" /> <Trans>Show message text</Trans>
                </DropdownMenuItem>
              )}
              {item.roomId !== My.user.id && (
                <DropdownMenuItem onClick={handleCopyToMyRoom} className="gap-2">
                  <Inbox className="w-4 h-4" /> <Trans>Copy to My Room</Trans>
                </DropdownMenuItem>
              )}
            </>
          )}

          {canEdit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { setEditingTitle(item.title || ""); setEditDialogOpen(true); }} className="gap-2">
                <Edit2 className="w-4 h-4" /> <Trans>Rename</Trans>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleTogglePin} className="gap-2">
                {item.pinned ? <><PinOff className="w-4 h-4" /> <Trans>Unpin</Trans></> : <><Pin className="w-4 h-4" /> <Trans>Pin</Trans></>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDeleteAlertOpen(true)} className="gap-2 text-red-600 focus:text-red-600 focus:bg-red-50">
                <Trash2 className="w-4 h-4" /> <Trans>Delete</Trans>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Rename item</Trans></DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="title" className="mb-2 block"><Trans>Title</Trans></Label>
            <Input id="title" value={editingTitle} onChange={(e) => setEditingTitle(e.target.value)} onKeyDown={(e) => UtilApp.runOnEnter(e as any, handleSaveTitle)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}><Trans>Cancel</Trans></Button>
            <Button onClick={handleSaveTitle}><Trans>Save</Trans></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={deleteAlertOpen} onOpenChange={setDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete link?</Trans></AlertDialogTitle>
            <AlertDialogDescription><Trans>Do you want to delete this file?</Trans></AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600"><Trans>Yes, Delete</Trans></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ItemPopover;